import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CurrencyInput, formatCurrency } from "@/components/ui/currency-input";
import { 
  Wallet, Plus, ChevronLeft, ChevronRight, Calendar, Banknote, Users, 
  MoreVertical, Check, X, AlertCircle, RefreshCw, Eye, Clock, Dumbbell, UserCog
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale, getMonthNames , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import { parseTurkishNumber, formatTurkishCurrency } from "@/utils/numberUtils";
import { useTranslation } from "react-i18next";

export default function SalaryPaymentsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages", "employees"]);

  const [loading, setLoading] = useState(true);
  const [pendingSalaries, setPendingSalaries] = useState({ trainer_salaries: [], employee_salaries: [], total_pending: 0 });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [selectedAccrual, setSelectedAccrual] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "cash",
    payment_date: todayLocalString(),
    notes: ""
  });
  const [ignoreReason, setIgnoreReason] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [viewType, setViewType] = useState("trainers"); // trainers | employees
  const [isSaving, setIsSaving] = useState(false); // Çift tıklama engeli
  
  // Date picker popover state for mobile
  const [salaryDatePickerOpen, setSalaryDatePickerOpen] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchData();
    }
  }, [businessId, selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch ALL pending salaries (no month filter) and payment history for selected month
      // Note: Backend automatically calculates accruals before returning data
      const [salariesRes, paymentsRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/pending-salaries`, {
          credentials: "include"
        }),
        fetch(`${API}/businesses/${businessId}/debt-payments?debt_type=salary&month=${selectedMonth}&year=${selectedYear}`, {
          credentials: "include"
        })
      ]);

      if (salariesRes.ok) {
        const data = await salariesRes.json();
        setPendingSalaries(data);
      }
      
      if (paymentsRes.ok) {
        const data = await paymentsRes.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t('common:dataLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAccruals = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/calculate-salary-accruals`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || t("common:accrualsUpdated"));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const openPaymentModal = (accrual) => {
    setSelectedAccrual(accrual);
    setPaymentForm({
      amount: accrual.remaining_amount?.toString() || "",
      payment_method: "cash",
      payment_date: todayLocalString(),
      notes: ""
    });
    setShowPaymentModal(true);
  };

  const openIgnoreModal = (accrual) => {
    setSelectedAccrual(accrual);
    setIgnoreReason("");
    setShowIgnoreModal(true);
  };

  const handleCreatePayment = async () => {
    if (!selectedAccrual || !paymentForm.amount) {
      toast.error("Tutar girmelisiniz");
      return;
    }

    // Çift tıklama engeli
    if (isSaving) {
      return;
    }

    // paymentForm.amount is now already a number from CurrencyInput
    const amount = typeof paymentForm.amount === 'number' ? paymentForm.amount : parseTurkishNumber(paymentForm.amount) || 0;
    if (amount <= 0) {
      toast.error(t("common:toast_tutar0danBuyukOlmali"));
      return;
    }
    if (amount > selectedAccrual.remaining_amount) {
      toast.error(`Tutar kalan borçtan (${formatCurrency(selectedAccrual.remaining_amount)}) fazla olamaz`);
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${API}/accrued-debts/${selectedAccrual.accrual_id}/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: amount,
          payment_date: paymentForm.payment_date,
          payment_method: paymentForm.payment_method,
          notes: paymentForm.notes
        })
      });

      if (response.ok) {
        toast.success(t("employees:toast_maasOdemesiKaydedildi"));
        setShowPaymentModal(false);
        setSelectedAccrual(null);
        setPaymentForm({ amount: "", payment_method: "cash", payment_date: todayLocalString(), notes: "" });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:couldNotSavePayment"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleIgnoreAccrual = async () => {
    if (!selectedAccrual) return;

    try {
      const response = await fetch(`${API}/accrued-debts/${selectedAccrual.accrual_id}/ignore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: ignoreReason })
      });

      if (response.ok) {
        toast.success(t("common:toast_borcYoksayildi"));
        setShowIgnoreModal(false);
        setSelectedAccrual(null);
        setIgnoreReason("");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: t("payments:cash"),
      credit_card: t("payments:creditCard"),
      bank_transfer: t("payments:bankTransfer"),
      eft: t("payments:eft")
    };
    return labels[method] || method;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-orange-100 text-orange-700 text-xs">Bekliyor</Badge>;
      case "partial":
        return <Badge className="bg-blue-100 text-blue-700 text-xs">Kısmi Ödendi</Badge>;
      case "paid":
        return <Badge className="bg-green-100 text-green-700 text-xs">{t('payments:paid')}</Badge>;
      case "ignored":
        return <Badge className="bg-gray-100 text-gray-700 text-xs">Yoksayıldı</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const currentSalaries = viewType === "trainers" ? pendingSalaries.trainer_salaries : pendingSalaries.employee_salaries;
  const trainerCount = pendingSalaries.trainer_salaries?.length || 0;
  const employeeCount = pendingSalaries.employee_salaries?.length || 0;
  const trainerTotal = pendingSalaries.trainer_salaries?.reduce((sum, s) => sum + (s.remaining_amount || 0), 0) || 0;
  const employeeTotal = pendingSalaries.employee_salaries?.reduce((sum, s) => sum + (s.remaining_amount || 0), 0) || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('payments:salaryPayments')}</h2>
          <p className="text-grey text-sm">{t("employees:antrenorVeCalisanMaasHakedislerini")}</p>
        </div>

        <div className="flex items-center gap-2">
          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAccruals}
            className="rounded-lg"
            data-testid="refresh-accruals-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common:update')}
          </Button>

          {/* Month Navigation */}
          <div className="flex items-center gap-2 bg-white rounded-xl border p-2">
            <Button variant="ghost" size="sm" onClick={handlePreviousMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-[140px] justify-center">
              <Calendar className="w-4 h-4 text-navy" />
              <span className="font-medium text-navy">
                {getMonthNames()[selectedMonth - 1]} {selectedYear}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleNextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Dumbbell className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("employees:antrenorMaasi")}</p>
                <p className="font-bold text-lg text-navy">{trainerCount} {t("common:person")}</p>
                <p className="text-xs text-orange-600">{formatTurkishCurrency(trainerTotal)} {t("common:pending")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserCog className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("employees:calisanMaasi")}</p>
                <p className="font-bold text-lg text-navy">{employeeCount} {t("common:person")}</p>
                <p className="text-xs text-orange-600">{formatTurkishCurrency(employeeTotal)} {t("common:pending")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t('common:totalPending')}</p>
                <p className="font-bold text-lg text-orange-600">
                  {formatTurkishCurrency((pendingSalaries.total_pending || 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("common:buAyOdenen")}</p>
                <p className="font-bold text-lg text-green-600">
                  {formatTurkishCurrency(paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
          <TabsTrigger value="pending" data-testid="pending-tab">
            <Clock className="w-4 h-4 mr-2" />
            {t('common:pendingPaymentHistory')}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            <Check className="w-4 h-4 mr-2" />
            {t('common:paymentHistory')}
          </TabsTrigger>
        </TabsList>

        {/* Pending Payments Tab */}
        <TabsContent value="pending" className="space-y-4">
          {/* View Type Selector */}
          <div className="flex gap-2">
            <Button
              variant={viewType === "trainers" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewType("trainers")}
              className={viewType === "trainers" ? "bg-navy" : ""}
              data-testid="view-trainers-btn"
            >
              <Dumbbell className="w-4 h-4 mr-2" />
              {t('common:trainersTab')} ({trainerCount})
            </Button>
            <Button
              variant={viewType === "employees" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewType("employees")}
              className={viewType === "employees" ? "bg-navy" : ""}
              data-testid="view-employees-btn"
            >
              <UserCog className="w-4 h-4 mr-2" />
              {t('employees:employeesTab')} ({employeeCount})
            </Button>
          </div>

          {/* Pending Salaries Table */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common:person")}</TableHead>
                      <TableHead>{t("common:period")}</TableHead>
                      <TableHead className="text-right">{t('trainers:salary')}</TableHead>
                      <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                      <TableHead className="text-right">{t('packages:remaining')}</TableHead>
                      <TableHead>{t('common:status')}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentSalaries?.map((salary) => (
                      <TableRow key={salary.accrual_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-navy text-white text-xs">
                                {salary.entity_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{salary.entity_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {getMonthNames()[(salary.month || 1) - 1]} {salary.year}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatTurkishCurrency((salary.amount || 0))}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatTurkishCurrency((salary.paid_amount || 0))}
                        </TableCell>
                        <TableCell className="text-right font-medium text-orange-600">
                          {formatTurkishCurrency((salary.remaining_amount || 0))}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(salary.status)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              className="bg-navy hover:bg-navy/90 rounded-lg"
                              onClick={() => openPaymentModal(salary)}
                              data-testid={`pay-salary-${salary.accrual_id}`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              {t("common:pay")}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openIgnoreModal(salary)}>
                                  <X className="w-4 h-4 mr-2" />
                                  Yoksay
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!currentSalaries || currentSalaries.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-grey">
                          {viewType === "trainers" 
                            ? t("employees:noPendingTrainerSalary")
                            : t("employees:noPendingEmployeeSalary")
                          }
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common:date')}</TableHead>
                      <TableHead>{t("common:person")}</TableHead>
                      <TableHead>{t("common:period")}</TableHead>
                      <TableHead>{t('payments:paymentMethod')}</TableHead>
                      <TableHead className="text-right">{t('common:amount')}</TableHead>
                      <TableHead>{t('common:note')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentHistory.map((payment) => (
                      <TableRow key={payment.payment_id}>
                        <TableCell className="text-sm">
                          {payment.payment_date ? format(new Date(payment.payment_date), "dd MMM yyyy", { locale: getDateLocale() }) : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {payment.entity_type === "trainer" ? t('common:trainer') : t("employees:employee")}
                            </Badge>
                            <span className="font-medium">{payment.entity_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getMonthNames()[(payment.month || 1) - 1]} {payment.year}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {getPaymentMethodLabel(payment.payment_method)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold text-green-600">
                          {formatTurkishCurrency((payment.amount || 0))}
                        </TableCell>
                        <TableCell className="text-sm text-grey max-w-[200px] truncate">
                          {payment.notes || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {paymentHistory.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-grey">
                          {t("common:noPaymentRecordThisPeriod")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("employees:maasOdemesiKaydet")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedAccrual && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-navy text-white">
                      {selectedAccrual.entity_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedAccrual.entity_name}</p>
                    <p className="text-sm text-grey">
                      {getMonthNames()[(selectedAccrual.month || 1) - 1]} {selectedAccrual.year} - {t('packages:remaining')}: {formatTurkishCurrency((selectedAccrual.remaining_amount || 0))}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t("common:odemeTutari")}</Label>
              <CurrencyInput
                value={paymentForm.amount}
                onChange={(val) => setPaymentForm({ ...paymentForm, amount: val })}
                placeholder="0"
                className="rounded-xl"
                data-testid="payment-amount-input"
              />
              <p className="text-xs text-grey">
                Kalan borç: {formatCurrency(selectedAccrual?.remaining_amount || 0)}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t("common:paymentDate")}</Label>
              <Popover open={salaryDatePickerOpen} onOpenChange={setSalaryDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="payment-date-input"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {paymentForm.payment_date ? (
                      format(new Date(paymentForm.payment_date), "dd MMMM yyyy", { locale: getDateLocale() })
                    ) : (
                      <span className="text-grey">{t('common:selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={paymentForm.payment_date ? new Date(paymentForm.payment_date) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const dateStr = format(date, "yyyy-MM-dd");
                        setPaymentForm({ ...paymentForm, payment_date: dateStr });
                        setSalaryDatePickerOpen(false);
                      }
                    }}
                    locale={tr}
                    initialFocus={false}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>{t('payments:paymentMethod')}</Label>
              <Select
                value={paymentForm.payment_method}
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
              >
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                  <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("common:noteOptional")}</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder={t("common:ph_odemeIleIlgiliNot")}
                rows={2}
                data-testid="payment-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowPaymentModal(false)} disabled={isSaving}>
              {t("common:cancel")}
            </Button>
            <Button 
              onClick={handleCreatePayment} 
              className="bg-navy hover:bg-navy/90"
              data-testid="confirm-payment-btn"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t("common:savePayment2")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ignore Modal */}
      <Dialog open={showIgnoreModal} onOpenChange={setShowIgnoreModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              {t("employees:ignoreSalaryDebt")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedAccrual && (
              <div className="p-3 bg-orange-50 rounded-xl">
                <p className="text-sm text-orange-700">
                  <strong>{selectedAccrual.entity_name}</strong> {t("common:forEntity")}{" "}
                  <strong>{getMonthNames()[(selectedAccrual.month || 1) - 1]} {selectedAccrual.year}</strong> {t('common:periodOf')}{" "}
                  <strong>{formatTurkishCurrency((selectedAccrual.remaining_amount || 0))}</strong> tutarındaki borç yoksayılacak.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Yoksayma Sebebi (Opsiyonel)</Label>
              <Textarea
                value={ignoreReason}
                onChange={(e) => setIgnoreReason(e.target.value)}
                placeholder={t("common:ph_nedenYoksayildiginiNotEdin")}
                rows={3}
                data-testid="ignore-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowIgnoreModal(false)}>
              {t("common:cancel")}
            </Button>
            <Button 
              onClick={handleIgnoreAccrual} 
              variant="destructive"
              data-testid="confirm-ignore-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Yoksay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
